export default `---
slug: agentforce-chatbot
title: "Agentforce: The Chatbot That Almost Was"
year: 2025
category: work
featured: false
tags: [Salesforce, Agentforce, GenAI, RAG, Project Management, AI Governance, Stakeholder Management]
thumbnail: /images/projects/agentforce_chatbot.jpg
description: "First customer-facing GenAI project at Swift. Led from POC to pilot-ready — then a reorg happened. The chatbot never shipped. The lessons never left."
relatedLinks:
  - label: "Salesforce Prompt Builder"
    href: /projects/salesforce-prompt-builder
  - label: "AI Knowledge Readiness Framework"
    href: /projects/ai-knowledge-readiness-framework
  - label: "Swift — Enterprise AI"
    href: /career/swift-enterprise-ai
---

![Agentforce chatbot project](/images/projects/agentforce_chatbot.jpg)

There's a particular kind of project that never makes it to production but still changes everything. This was that project.

In 2025, I led the development of a **customer-facing AI chatbot** built on Salesforce Agentforce — the first attempt in the company's history to put generative AI directly in front of customers. It was my **first project leadership role**, and it was never deployed. And I'd do it again without hesitation.

## The Mission

The chatbot's domain was the **Customer Security Programme** — Swift's resource hub for helping financial institutions strengthen their cybersecurity posture. As a knowledge domain, it doesn't get much more sensitive than this: the accuracy bar was exceptionally high, the subject matter was technical and consequential, and the user base was exactly the kind of audience that doesn't tolerate hallucinations.

This made the project both exciting and genuinely hard.

## What We Built

The technical stack centred on **Salesforce Agentforce**, working alongside Salesforce Professional Services. My role covered the full project lifecycle: kickoff, stakeholder alignment across multiple levels of the organisation, day-to-day management of the collaboration with the Salesforce PS team, and organization and management of the testing.

On the Agentforce side, the architecture relied on **topics** — Agentforce's configuration layer that defines what the agent knows, how it retrieves knowledge, and what guardrails it operates within. Setting up the environment alone was a non-trivial exercise. Getting it to work coherently with our knowledge base took considerably more.

We ran a **proof of concept**, tuned the instructions, and started evaluating responses systematically.

## The Diagnosis

Testing revealed a pattern: certain answers were consistently wrong. Not randomly wrong — *systematically* wrong, in predictable ways, on specific question types.

We traced the root cause to **chunk retrieval**. The knowledge source was a set of PDF documents, and the way those PDFs were being parsed — the way the text was being chunked before ingestion into the retrieval pipeline — was introducing structural errors. The AI was retrieving the right documents but the wrong fragments, and generating confident, coherent, incorrect answers as a result.

The fix wasn't in the model. **It was in the documents.**

We went through the source material and reformatted every problematic file — restructuring sections, cleaning up tables, removing formatting artefacts that confused the parser. It was painstaking work, but it worked. After remediation, response quality came in line with expectations for the first time.

## Almost There

We were preparing to launch an internal pilot when a reorganisation shifted the company's priorities. The project was paused, and then quietly shelved.

The chatbot was never deployed. Not because it failed — it had, by that point, cleared the quality bar we'd set — but because the context around it changed.

## The Harder Conversation

What I didn't fully anticipate going in was the **governance dimension**.

This was the company's first serious attempt at customer-facing generative AI. The conversations with governance and risk partners were everything but easy. A highly regulated financial infrastructure company, dealing with a sensitive topic, wanting to put an AI in front of customers: every stakeholder had questions, and most of the questions were the right ones.

We weren't mature enough, as an organisation, to clear the bar we'd collectively set — and in retrospect, that bar was exactly where it should have been. The project not continuing made sense. But the *process* of having those conversations, of working through what responsible deployment of GenAI actually looks like in practice, raised the collective understanding across teams in ways that a smooth launch never would have.

## What I Took Away

This project gave me three things I've carried into everything since:

**Stakeholder management at scale.** Engaging across levels and functions, building alignment on something new and genuinely uncertain, keeping momentum without overselling — this was a crash course, and I loved it.

**A rigorous approach to AI testing.** Systematic evaluation, failure categorisation, root cause analysis on bad outputs — the discipline required to actually validate a GenAI system is underappreciated, and I learned it here.

**A visceral understanding of knowledge quality.** The PDF parsing bug wasn't a technical fluke — it exposed a fundamental truth about RAG systems: the quality of the retrieval is a direct function of how well the source material is prepared. That lesson became its own project.

*And yes — the knowledge side of things eventually turned into the [AI Knowledge Readiness Framework](#/projects/ai-knowledge-readiness-framework).*
`;
